(ns manage.adset-decision-tree-page.rotation
  (:require [clojure.string :as str]
            [medley.core :as medley]))

(def legacy-node-type :decision-tree-node/variants-rotation)
(def weighted-node-type :decision-tree-node/variants-rotation-weighted)

(def rotation-node-types #{legacy-node-type weighted-node-type})

(defn- normalize-type [type]
  (cond
    (keyword? type) type
    (string? type) (keyword "decision-tree-node" type)
    :else type))

(defn variants-rotation? [{:keys [type]}]
  (contains? rotation-node-types (normalize-type type)))

(defn weighted-node? [{:keys [type]}]
  (let [type (normalize-type type)]
    (or (= type weighted-node-type)
        (= type legacy-node-type))))

(defn distribution-strategy [config]
  (get-in config [:distribution :strategy]))

(defn root-even-rotation? [{:keys [config]}]
  (or (= "even" (distribution-strategy config))
      (= :even (keyword (get-in config [:rotation :mode])))))

(defn root-weighted-distribution? [{:keys [config]}]
  (= "weighted" (distribution-strategy config)))

(defn apply-distribution
  "Sets the root node's distribution strategy, dropping the legacy :rotation config."
  ([config strategy] (apply-distribution config strategy nil))
  ([config strategy weights]
   (-> (or config {})
       (dissoc :rotation)
       (assoc :distribution (cond-> {:strategy strategy} weights (assoc :weights weights))))))

(defn clear-root-distribution-config [config]
  (-> (or config {})
      (dissoc :distribution :rotation)))

(defn equal-weight [path-count]
  (max 1 (quot 100 path-count)))

(def weighted-default-weight 1)
(def default-path-config {:rotation {:weight weighted-default-weight}})

(defn path-display-weight [parent-data outcome-config path-count]
  (if (weighted-node? parent-data)
    (get-in outcome-config [:rotation :weight])
    (equal-weight path-count)))

(defn has-path-ad-variants? [{:keys [outcome-node-ids]} node-id->node-data]
  (some #(seq (:ad-variants (get node-id->node-data %))) outcome-node-ids))

(defn any-path-connected? [{:keys [outcome-node-ids]} node-id->children]
  (some #(seq (get node-id->children %)) outcome-node-ids))

(defn rotation-parent-for-outcome [outcome-id node-id->node-data]
  (when-let [parent-id (:parent-id (get node-id->node-data outcome-id))]
    (let [parent (get node-id->node-data parent-id)]
      (when (variants-rotation? parent) parent))))

(defn weighted-rotation-path-outcome? [outcome-id node-id->node-data]
  (when-let [parent (rotation-parent-for-outcome outcome-id node-id->node-data)]
    (weighted-node? parent)))

(defn connect-blocked-for-outcome? [outcome-id node-id->node-data]
  (when-let [parent (rotation-parent-for-outcome outcome-id node-id->node-data)]
    (has-path-ad-variants? parent node-id->node-data)))

(defn setup-complete? [{:keys [outcome-node-ids]}]
  (>= (count outcome-node-ids) 2))

(defn initial-paths []
  (map-indexed (fn [i letter]
                 {:label (str "Path " letter)
                  :config default-path-config
                  :ordinal (inc i)})
               ["A" "B"]))

(defn remaining-initial-paths [rotation-node]
  (drop (count (:outcome-node-ids rotation-node)) (initial-paths)))

(defn weighted-node-linked-to-root? [root-node-id {:keys [parent-id]}]
  (= parent-id root-node-id))

(defn find-root-weighted-rotation-node [root-node-id node-id->node-data]
  (some (fn [node]
          (when (and (weighted-node? node)
                     (weighted-node-linked-to-root? root-node-id node))
            node))
        (vals node-id->node-data)))

(defn find-weighted-rotation-node
  [root-node-id node-id->node-data]
  (or (find-root-weighted-rotation-node root-node-id node-id->node-data)
      (some #(when (weighted-node? %) %) (vals node-id->node-data))))

(defn weighted-rotation-node-ids [node-id->node-data]
  (->> node-id->node-data vals (filter weighted-node?) (map :id) vec))

(defn next-path-label [outcome-ids node-id->node-data]
  (some (fn [letter]
          (let [label (str "Path " letter)]
            (when-not (some #(= label (:label %)) (map node-id->node-data outcome-ids))
              label)))
        "ABCDEFGHIJKLMNOPQRSTUVWXYZ"))

(defn- variant-uuid-str [{:keys [ad-variant-uuid uuid]}]
  (some-> (or ad-variant-uuid uuid) str))

(defn- outcome-path-weight [outcome-node path-count]
  (or (get-in outcome-node [:config :rotation :weight])
      weighted-default-weight
      (equal-weight path-count)))

(defn collect-path-variant-weights
  [rotation-node node-id->node-data]
  (let [path-count (count (:outcome-node-ids rotation-node))]
    (for [outcome-id (:outcome-node-ids rotation-node)
          :let [outcome (get node-id->node-data outcome-id)
                variants (:ad-variants outcome)
                path-weight (outcome-path-weight outcome path-count)
                variants-per-path (max 1 (count variants))]
          variant variants
          :let [ad-variant-uuid (variant-uuid-str variant)]
          :when (not (str/blank? ad-variant-uuid))]
      {:ad-variant-uuid ad-variant-uuid
       :weight (/ path-weight variants-per-path)})))

(defn- normalize-distribution-weights [weight-by-uuid]
  (let [total (reduce + (vals weight-by-uuid))]
    (when (pos? total)
      (update-vals weight-by-uuid #(/ (double %) total)))))

(defn build-distribution-weights
  [rotation-node node-id->node-data]
  (let [grouped (reduce (fn [acc {:keys [ad-variant-uuid weight]}]
                          (update acc ad-variant-uuid (fnil + 0) weight))
                        {}
                        (collect-path-variant-weights rotation-node node-id->node-data))]
    (normalize-distribution-weights grouped)))

(defn collect-rotation-ad-variants [rotation-node node-id->node-data]
  (->> (:outcome-node-ids rotation-node)
       (mapcat (fn [outcome-id] (:ad-variants (get node-id->node-data outcome-id))))
       (reduce (fn [acc variant]
                 (if-let [uuid (:uuid variant)]
                   (assoc acc uuid variant)
                   acc))
               {})))

(defn merge-root-ad-variants [root-ad-variants path-ad-variants-by-uuid]
  (vals (merge (medley/index-by :uuid root-ad-variants)
               path-ad-variants-by-uuid)))

(defn root-weighted-distribution-patch
  "Builds a root node patch from the rotation paths' ad variants and weights."
  [root-node-data rotation-node node-id->node-data]
  (when-let [weights (build-distribution-weights rotation-node node-id->node-data)]
    (let [root-config (or (:config root-node-data) {})
          path-variants (collect-rotation-ad-variants rotation-node node-id->node-data)
          merged-ad-variants (merge-root-ad-variants (or (:ad-variants root-node-data) [])
                                                     path-variants)]
      {:ad-variants (mapv #(select-keys % [:uuid]) merged-ad-variants)
       :config (apply-distribution root-config "weighted" weights)})))
